// Ported from app/src/lib/routing.ts — regex-only. LLM fallback is native-only
// (Qwen 1.7B won't run usably in a browser). If a user's utterance doesn't
// match a command regex, we fall back to plain speak with best-effort routing.
import type { CrewMember } from '../state/session';

export type RouteResult = {
  recipientId: string | null;
  displayTranscript: string;
  urgency: 'urgent' | null;
};

export type VoiceCommand =
  | { kind: 'mark_place'; name: string }
  | { kind: 'where_is'; target: string }
  | { kind: 'roll_call' }
  | { kind: 'who_closest'; target: string | null }
  | { kind: 'meet_at'; placeName: string }
  | { kind: 'set_presence'; status: 'online' | 'offline' };

const HEY_RE = /^\s*(?:hey|hi|hello|yo)[,\s]+([a-z][a-z'-]*)[,.\s]*/i;
const URGENT_RE = /^\s*(urgent|emergency|hey\s+everyone|alert)[,:.!\s-]+/i;
const MARK_PLACE_RE =
  /^\s*(?:mark|save|pin|remember|call|name)\s+this\s+(?:as\s+|the\s+)?(.+?)[.?!]?\s*$/i;
const WHERE_IS_RE = /^\s*where(?:'s|\s+is|\s+are)\s+(?:the\s+)?(.+?)\??\s*$/i;
const GO_OFFLINE_RE =
  /^\s*(?:i[' ]?m\s+)?(?:going\s+offline|offline\s+me|signing\s+off|heading\s+out)\b/i;
const GO_ONLINE_RE = /^\s*(?:i[' ]?m\s+)?(?:back\s+online|online\s+me|i[' ]?m\s+back|back\s+on)\b/i;
const ROLL_CALL_RE =
  /^\s*(?:start\s+(?:a\s+)?(?:roll\s*call|check[- ]?in)|roll\s*call|check[- ]?in\s+everyone|everyone\s+check[- ]?in)\b/i;
const WHO_CLOSEST_RE =
  /^\s*who(?:'s|\s+is)\s+(?:the\s+)?closest(?:\s+(?:to|from)\s+(?:the\s+)?(.+?))?[.?!]?\s*$/i;
const MEET_AT_RE = /^\s*(?:meet|rendezvous|head)\s+(?:up\s+)?(?:at|to)\s+(?:the\s+)?(.+?)[.?!]?\s*$/i;
const EVERYONE_HEAD_RE =
  /^\s*everyone\s+(?:meet|head)\s+(?:at|to)\s+(?:the\s+)?(.+?)[.?!]?\s*$/i;

function norm(s: string) {
  return s.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function parseVoiceCommand(transcript: string | null): VoiceCommand | null {
  const text = (transcript ?? '').trim();
  if (!text) return null;

  const mark = text.match(MARK_PLACE_RE);
  if (mark) {
    const name = mark[1].trim().replace(/^(the|a|an)\s+/i, '');
    if (name.length > 0 && name.length < 40) return { kind: 'mark_place', name };
  }
  if (GO_OFFLINE_RE.test(text)) return { kind: 'set_presence', status: 'offline' };
  if (GO_ONLINE_RE.test(text)) return { kind: 'set_presence', status: 'online' };
  if (ROLL_CALL_RE.test(text)) return { kind: 'roll_call' };

  const closest = text.match(WHO_CLOSEST_RE);
  if (closest) {
    const raw = closest[1]?.trim();
    const target = raw && raw.length > 0 && raw.length < 40 ? raw : null;
    if (!target || /^(me|here|my\s+location)$/i.test(target)) {
      return { kind: 'who_closest', target: null };
    }
    return { kind: 'who_closest', target };
  }

  const meetE = text.match(EVERYONE_HEAD_RE) ?? text.match(MEET_AT_RE);
  if (meetE) {
    const placeName = meetE[1].trim().replace(/^(the|a|an)\s+/i, '');
    if (placeName.length > 0 && placeName.length < 40) return { kind: 'meet_at', placeName };
  }

  const where = text.match(WHERE_IS_RE);
  if (where) {
    const target = where[1].trim();
    if (target.length > 0 && target.length < 40) return { kind: 'where_is', target };
  }

  return null;
}

export function routeMessage(
  transcript: string | null,
  crew: CrewMember[],
  selfId: string
): RouteResult {
  let text = (transcript ?? '').trim();
  if (!text) return { recipientId: null, displayTranscript: text, urgency: null };

  let urgency: 'urgent' | null = null;
  const urgentMatch = text.match(URGENT_RE);
  if (urgentMatch) {
    urgency = 'urgent';
    text = text.slice(urgentMatch[0].length).trim();
  }

  const m = text.match(HEY_RE);
  if (!m) return { recipientId: null, displayTranscript: text, urgency };

  const target = norm(m[1]);
  if (!target) return { recipientId: null, displayTranscript: text, urgency };

  const match = crew.find((c) => {
    if (c.id === selfId) return false;
    if (norm(c.name) === target) return true;
    if (norm(c.display_name) === target) return true;
    return (c.aliases ?? []).some((a) => norm(a) === target);
  });
  if (!match) return { recipientId: null, displayTranscript: text, urgency };

  const stripped = text.slice(m[0].length).trim();
  return {
    recipientId: match.id,
    displayTranscript: stripped.length > 0 ? stripped : text,
    urgency,
  };
}

export function findCrewByToken(crew: CrewMember[], token: string): CrewMember | null {
  const t = norm(token);
  if (!t) return null;
  return (
    crew.find(
      (c) =>
        norm(c.name) === t ||
        norm(c.display_name) === t ||
        (c.aliases ?? []).some((a) => norm(a) === t)
    ) ?? null
  );
}
